import BookAuthorMutations from '@/graphql/BookAuthorMutations';
import IRIS, { createIri } from '@/iris';
import LocalBookQueries from '@/graphql/LocalBookQueries';

export default {
  methods: {
    /**
     * Add an author to a book
     * @param {String} bookId - The book ID (with or without IRI prefix)
     * @param {Object} author - Author object with id and name
     * @param {Object} role - Role object with id and description
     * @param {Number} sequence - Optional sequence number (default: 1)
     * @returns {Promise} - Promise resolving to the created BookAuthor
     */
    async addBookAuthor(bookId, author, role, sequence = 1) {
      try {
        // Use createIri to format IRIs properly
        const bookIri = createIri(IRIS.localBook, bookId);
        const authorIri = createIri(IRIS.author, author._id || author.id);
        const roleIri = createIri(IRIS.authorRole, role._id || role.id);
        
        // Execute mutation
        const result = await this.$apollo.mutate({
          mutation: BookAuthorMutations.createBookAuthor,
          variables: {
            book: bookIri,
            author: authorIri,
            role: roleIri,
            sequence: sequence
          },
          update: (cache, { data }) => {
            const newBookAuthor = data?.createBookAuthor?.bookAuthor;
            if (!newBookAuthor) return;

            const bookIri = createIri(IRIS.localBook, bookId);
            const bookCacheId = cache.identify({ __typename: 'LocalBook', id: bookId });

            if (bookCacheId) {
              try {
                cache.modify({
                  id: bookCacheId,
                  fields: {
                    bookAuthors(existingAuthorsRefs = [], { readField }) {
                      const newBookAuthorRef = cache.identify(newBookAuthor);

                      const alreadyExists = existingAuthorsRefs.some(
                        ref => readField('id', ref) === newBookAuthor.id
                      );
                      if (alreadyExists) {
                        return existingAuthorsRefs;
                      }

                      return [...existingAuthorsRefs, { __ref: newBookAuthorRef }];
                    }
                  }
                });
              } catch (error) {
                console.error('Error modifying cache after author addition:', error);
              }
            } else {
              console.warn(`LocalBook ${bookIri} not found in cache for adding author.`);
            }
          }
        });

        if (result.data && result.data.createBookAuthor) {
          return result.data.createBookAuthor.bookAuthor;
        }
        return null;
      } catch (error) {
        console.error('Error adding book author:', error);
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('admin.productsDatabase.importWizard.steps.productDetails.authorAddError', 'Error adding author to book'),
          color: 'error'
        });
        throw error;
      }
    },

    /**
     * Remove an author from a book
     * @param {String} bookAuthorId - The book author relationship ID to delete
     * @param {String} bookId - The book ID for cache update
     * @returns {Promise} - Promise resolving when deletion is complete
     */
    async removeBookAuthor(bookAuthorId, bookId) {
      try {

        const bookIri = createIri(IRIS.localBook, bookId);
        const result = await this.$apollo.mutate({
          mutation: BookAuthorMutations.deleteBookAuthor,
          variables: {
            id: bookAuthorId
          },
          update: (cache, { data }) => {
            const deletedAuthorId = data?.deleteBookAuthor?.bookAuthor?.id;
            const bookCacheId = cache.identify({ __typename: 'LocalBook', id: bookId  });

            if (deletedAuthorId && bookCacheId) {
              try {
                cache.modify({
                  id: bookCacheId,
                  fields: {
                    bookAuthors(existingAuthorsRefs = [], { readField }) {
                      return existingAuthorsRefs.filter(authorRef =>
                        readField('id', authorRef) !== deletedAuthorId
                      );
                    }
                  }
                });
              } catch (error) {
                console.error('Error modifying cache after author removal:', error);
              }
            }
          }
        });

        return result.data?.deleteBookAuthor?.bookAuthor;
      } catch (error) {
        console.error('Error removing book author:', error);
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('admin.productsDatabase.importWizard.steps.productDetails.authorRemoveError', 'Error removing author from book'),
          color: 'error'
        });
        throw error;
      }
    }
  }
};
